import { useId, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import type { FinanceExplorerValues } from "@chro/shared";
import { financeWaterfallAmount, financeWaterfallBridges, financeWaterfallDefaultLayout, financeWaterfallPlot, financeWaterfallTitles,
  type FinanceWaterfallBridgeId, type FinanceWaterfallKey, type FinanceWaterfallLabels, type FinanceWaterfallPlottedBar, type FinanceWaterfallStep } from "../../features/eex/pnl/finance-waterfall";
import "./finance-waterfall.css";

export interface FinanceWaterfallProps {
  /** Already projected Finance values; the chart never recomputes them. */
  values: FinanceExplorerValues;
  currency: string;
  /** Called with the Finance row behind the bar, so the existing explanation can open. */
  onSelect?: (key: FinanceWaterfallKey) => void;
  labels?: FinanceWaterfallLabels;
  titles?: Partial<Record<FinanceWaterfallBridgeId, string>>;
  /** Shown beside the legend: the month or range and currency the bridges describe. */
  scopeLabel?: string;
  note?: string;
  disabled?: boolean;
  testId?: string;
}

const CHART_HEIGHT = 176;
const LABEL_BAND = 54;
const MIN_COLUMN = 108;
const toneLabels = { add: "Adds", reduce: "Reduces", balance: "Balance or total", unavailable: "Unavailable" } as const;

function useMeasuredWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const update = () => setWidth(element.clientWidth);
    update();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

function Bar({ bar, zeroY, active }: { bar: FinanceWaterfallPlottedBar; zeroY: number; active: boolean }) {
  const className = `fin-wf-bar fin-wf-bar-${bar.tone}${bar.step.role === "subtotal" ? " fin-wf-bar-subtotal" : ""}${active ? " fin-wf-bar-active" : ""}`;
  if (!bar.available) return <rect className={className} x={bar.x} y={bar.yStart - 8} width={bar.barWidth} height={16} rx={2} />;
  if (bar.tone === "flat") return <line className={className} x1={bar.x} x2={bar.x + bar.barWidth} y1={bar.yEnd} y2={bar.yEnd} data-zero={bar.yEnd === zeroY || undefined} />;
  return <rect className={className} x={bar.x} y={bar.top} width={bar.barWidth} height={Math.max(2, bar.height)} rx={2} />;
}

function Bridge({ id, steps, title, currency, disabled, onSelect }: {
  id: FinanceWaterfallBridgeId; steps: FinanceWaterfallStep[]; title: string; currency: string; disabled: boolean; onSelect?: (key: FinanceWaterfallKey) => void;
}) {
  const titleId = useId();
  const [scrollRef, measured] = useMeasuredWidth();
  const [active, setActive] = useState<FinanceWaterfallKey | null>(null);
  const width = Math.max(measured, steps.length * MIN_COLUMN);
  const plot = financeWaterfallPlot(steps, { ...financeWaterfallDefaultLayout, width, chartHeight: CHART_HEIGHT });
  return <figure className="fin-wf-bridge" data-bridge={id} aria-labelledby={titleId}>
    <figcaption id={titleId} className="fin-wf-title">{title}</figcaption>
    <div className="fin-wf-scroll" ref={scrollRef}>
      <div className="fin-wf-plot" style={{ width, height: CHART_HEIGHT + LABEL_BAND }}>
        <svg className="fin-wf-svg" width={width} height={CHART_HEIGHT} viewBox={`0 0 ${width} ${CHART_HEIGHT}`} aria-hidden="true" focusable="false">
          <line className="fin-wf-zero" x1={0} x2={width} y1={plot.zeroY} y2={plot.zeroY} />
          {plot.bars.map(bar => <g key={bar.step.key}>
            {bar.connector && <line className="fin-wf-connector" x1={bar.connector.x1} x2={bar.connector.x2} y1={bar.connector.y} y2={bar.connector.y} />}
            <Bar bar={bar} zeroY={plot.zeroY} active={active === bar.step.key} />
          </g>)}
        </svg>
        <div className="fin-wf-bars" role="group" aria-label={`${title}, ${currency}`}>
          {plot.bars.map(bar => {
            const amount = financeWaterfallAmount(bar.step.value);
            return <button type="button" key={bar.step.key} className="fin-wf-hit" data-key={bar.step.key} data-tone={bar.tone} data-role={bar.step.role}
              style={{ left: bar.x - (bar.column - bar.barWidth) / 2, width: bar.column }}
              aria-label={`${bar.step.label}, ${bar.available ? amount : "unavailable"}`} disabled={disabled}
              onClick={() => onSelect?.(bar.step.key)} onMouseEnter={() => setActive(bar.step.key)} onMouseLeave={() => setActive(current => current === bar.step.key ? null : current)}
              onFocus={() => setActive(bar.step.key)} onBlur={() => setActive(current => current === bar.step.key ? null : current)}>
              <span className={`fin-wf-amount fin-wf-amount-${bar.placement}${bar.available && bar.step.value! < 0 ? " fin-wf-amount-negative" : ""}`} style={{ top: bar.labelY }} data-testid="finance-waterfall-amount">{amount}</span>
              <span className="fin-wf-label"><span className="fin-wf-operator" aria-hidden="true">{bar.step.role === "step" ? (bar.step.sign < 0 ? "−" : "+") : bar.step.role === "balance" ? "" : "="}</span>{bar.step.label}</span>
            </button>;
          })}
        </div>
      </div>
    </div>
  </figure>;
}

/** Two bridges from already projected Finance values: calculated VM to recognized
 * P&L, and opening carry to closing carry. Pure presentation; the explanation
 * behind any bar opens through the caller's existing selection. */
export function FinanceWaterfall({ values, currency, onSelect, labels, titles, scopeLabel, note, disabled = false, testId }: FinanceWaterfallProps) {
  const bridges = financeWaterfallBridges(values, labels);
  const unavailable = bridges.some(bridge => bridge.steps.some(step => step.value == null));
  const tones = (["add", "reduce", "balance", ...(unavailable ? ["unavailable" as const] : [])] as const);
  return <div className="fin-waterfall" data-testid={testId} style={{ "--fin-wf-band": `${LABEL_BAND}px` } as CSSProperties}>
    <div className="fin-wf-head">
      <p className="fin-wf-scope">{scopeLabel ? <>{scopeLabel} · </> : null}<span>Select a bar for its explanation</span></p>
      <ul className="fin-wf-legend" aria-label="Chart colours">{tones.map(tone => <li key={tone}><i className={`fin-wf-swatch fin-wf-swatch-${tone}`} aria-hidden="true" />{toneLabels[tone]}</li>)}</ul>
    </div>
    {bridges.map(bridge => <Bridge key={bridge.id} id={bridge.id} steps={bridge.steps} title={titles?.[bridge.id] ?? financeWaterfallTitles[bridge.id]} currency={currency} disabled={disabled} onSelect={onSelect} />)}
    <p className="fin-wf-note">Amounts in {currency}. Steps float from the previous level; balances, subtotals and totals stand on zero.{unavailable ? " A dashed bar is unavailable, not zero." : ""}{note ? ` ${note}` : ""}</p>
  </div>;
}
